import { useCurrencies } from '@/hooks/useCurrencies';
import { type ColumnDef, type SortingState } from '@tanstack/react-table';
import { useMemo, useCallback } from 'react';
import { DataTable } from '@/components/data-display';
import { Badge, type BadgeVariant } from '@/components/data-display/Badge';
import { Pagination } from '@/components/data-display/Pagination';
import { Input } from '@/components/common';
import { useMonitoringStore } from '@/store/monitoringStore';
import { formatAmount } from '@/utils/formatters';
import { FilterPills } from './FilterPills';
import type { Instruction, InstructionStatus, PaginationMeta, SortField } from './types';

// ---------------------------------------------------------------------------
// Status badge colour mapping — matches legacy CSS
// ---------------------------------------------------------------------------

const STATUS_BADGE_MAP: Record<
  Exclude<InstructionStatus, 'all'>,
  { label: string; variant: BadgeVariant }
> = {
  pending: { label: 'Pending Review', variant: 'warning' },
  processing: { label: 'Processing', variant: 'info' },
  completed: { label: 'Completed', variant: 'success' },
  held: { label: 'On Hold', variant: 'error' },
  declined: { label: 'Declined', variant: 'default' },
  failed: { label: 'Failed', variant: 'error' },
  pending_client_approval: { label: 'Pending Client Approval', variant: 'violet' },
};

// ---------------------------------------------------------------------------
// Compliance mini-badge
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Currencies for filter dropdown
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface InstructionListProps {
  data: Instruction[];
  pagination: PaginationMeta | undefined;
  isLoading: boolean;
  onRowClick: (instruction: Instruction) => void;
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

function useColumns(onRowClick: (i: Instruction) => void): ColumnDef<Instruction, unknown>[] {
  return useMemo<ColumnDef<Instruction, unknown>[]>(
    () => [
      {
        accessorKey: 'client_name',
        header: 'Client',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="font-medium text-[#333]">{row.original.client_name}</span>
        ),
      },
      {
        accessorKey: 'beneficiary_label',
        header: 'Beneficiary',
        enableSorting: false,
        cell: ({ row }) => <span className="text-[#555]">{row.original.beneficiary_label}</span>,
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="font-semibold text-[#333] tabular-nums">
            {formatAmount(row.original.amount, row.original.currency)}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Date',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-[#666]">{formatDate(row.original.created_at)}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        cell: ({ row }) => {
          const cfg = STATUS_BADGE_MAP[row.original.status];
          return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
        },
      },
      {
        accessorKey: 'compliance_status',
        header: 'Compliance',
        enableSorting: false,
        cell: () => (
          <span
            className="text-xs text-[#999]"
            title="The payment API does not supply an authoritative screening summary"
          >
            Unavailable
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            className="text-xs font-medium text-[#6B0F1A] hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onRowClick(row.original);
            }}
          >
            Review
          </button>
        ),
      },
    ],
    [onRowClick],
  );
}

// ---------------------------------------------------------------------------
// Sort field mapping: TanStack uses column id, backend uses sort_by string
// ---------------------------------------------------------------------------

const SORTABLE_COLUMNS: Record<string, SortField> = {
  client_name: 'client_name',
  amount: 'amount',
  created_at: 'created_at',
  status: 'status',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InstructionList({ data, pagination, isLoading, onRowClick }: InstructionListProps) {
  const { codes: currencyCodes, unavailable: currenciesUnavailable } = useCurrencies();
  const status = useMonitoringStore((s) => s.status);
  const search = useMonitoringStore((s) => s.search);
  const date_from = useMonitoringStore((s) => s.date_from);
  const date_to = useMonitoringStore((s) => s.date_to);
  const currency = useMonitoringStore((s) => s.currency);
  const page = useMonitoringStore((s) => s.page);
  const limit = useMonitoringStore((s) => s.limit);
  const sort_by = useMonitoringStore((s) => s.sort_by);
  const sort_order = useMonitoringStore((s) => s.sort_order);

  const setStatus = useMonitoringStore((s) => s.setStatus);
  const setSearch = useMonitoringStore((s) => s.setSearch);
  const setDateFrom = useMonitoringStore((s) => s.setDateFrom);
  const setDateTo = useMonitoringStore((s) => s.setDateTo);
  const setCurrency = useMonitoringStore((s) => s.setCurrency);
  const setPage = useMonitoringStore((s) => s.setPage);
  const setLimit = useMonitoringStore((s) => s.setLimit);
  const setSort = useMonitoringStore((s) => s.setSort);
  const resetFilters = useMonitoringStore((s) => s.resetFilters);

  const columns = useColumns(onRowClick);

  // Convert store sort state to TanStack SortingState
  const sorting: SortingState = useMemo(
    () => [{ id: sort_by, desc: sort_order === 'desc' }],
    [sort_by, sort_order],
  );

  const handleSortingChange = useCallback(
    (next: SortingState) => {
      if (next.length > 0) {
        const col = next[0];
        const field = SORTABLE_COLUMNS[col.id];
        if (field) {
          setSort(field, col.desc ? 'desc' : 'asc');
        }
      }
    },
    [setSort],
  );

  const hasActiveFilters = search || date_from || date_to || currency || status !== 'all';

  return (
    <div className="space-y-4">
      {/* Filter pills */}
      <FilterPills selected={status} onChange={setStatus} />

      {/* Filter controls row */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Search */}
        <div className="relative min-w-[260px] flex-1">
          <Input
            placeholder="Search by client, beneficiary, or reference"
            value={search}
            onChange={setSearch}
            aria-label="Search instructions"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#333]"
              aria-label="Clear search"
            >
              &#x2715;
            </button>
          )}
        </div>

        {/* Date from */}
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
            From
          </label>
          <input
            type="date"
            value={date_from}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-lg border border-[#e0e0e0] px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
            aria-label="Filter from date"
          />
        </div>

        {/* Date to */}
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
            To
          </label>
          <input
            type="date"
            value={date_to}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-lg border border-[#e0e0e0] px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
            aria-label="Filter to date"
          />
        </div>

        {/* Currency */}
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
            Currency
          </label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
            disabled={currenciesUnavailable}
            aria-label="Filter by currency"
          >
            <option value="">All currencies</option>
            {currencyCodes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="rounded-lg border border-[#ddd] bg-white px-3 py-2.5 text-xs font-medium text-[#666] transition-colors hover:border-[#6B0F1A] hover:text-[#6B0F1A]"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      {!isLoading && data.length === 0 ? (
        <div className="rounded-[10px] bg-white px-7 py-[60px] text-center shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
          <div className="mb-3 text-4xl">&#x1F4CB;</div>
          <p className="text-sm text-[#bbb]">
            {hasActiveFilters
              ? 'No instructions found matching your criteria'
              : 'No instructions yet \u2014 waiting for client submissions from the Portal'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="mt-3 text-xs font-medium text-[#6B0F1A] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          isLoading={isLoading}
          rowClick={onRowClick}
        />
      )}

      {/* Pagination */}
      {pagination && pagination.total > 0 && (
        <div className="flex items-center justify-between text-[13px] text-[#666]">
          <span>
            Showing {Math.min((page - 1) * limit + 1, pagination.total)}
            {'\u2013'}
            {Math.min(page * limit, pagination.total)} of {pagination.total}
          </span>
          <Pagination
            pageIndex={page}
            pageSize={limit}
            totalRows={pagination.total}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
          />
        </div>
      )}
    </div>
  );
}
